import "./ReservationAssetShippingStatuses.scss"
import IconDeliveryNotForwardedNotReleased from "assets/img/shipping-status/delivery-not-forwarded-not-released.svg?react"
import IconDeliveryNotForwardedReleased from "assets/img/shipping-status/delivery-not-forwarded-released.svg?react"
import IconDeliveryForwardedNotReleased from "assets/img/shipping-status/delivery-forwarded-not-released.svg?react"
import IconDeliveryReleased from "assets/img/shipping-status/delivery-released.svg?react"
import IconReturnNotForwardedNotStored from "assets/img/shipping-status/return-not-forwarded-not-stored.svg?react"
import IconReturnNotForwardedStored from "assets/img/shipping-status/return-not-forwarded-stored.svg?react"
import IconReturnForwardedNotStored from "assets/img/shipping-status/return-forwarded-not-stored.svg?react"
import IconReturnStored from "assets/img/shipping-status/return-stored.svg?react"

import { FC, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Dayjs } from "utils/dayjs"

import Tooltip from "components/Tooltip/Tooltip"
import ReservationAsset from "models/ReservationAsset"
import { ShippingOrderType } from "models/ShippingOrder"
import ReservationCartAsset from "models/ReservationCartAsset"
import { Purpose } from "config/constants"
import { Flex, FlexItem } from "components/Flex"
import ShippingOrderAsset from "models/ShippingOrderAsset"

interface IReservationAssetShippingStatusesProps {
    asset: ReservationAsset | ReservationCartAsset
    purpose?: Purpose
    wrap?: boolean
    prependIcons?: IStatusIcon[]
}

export interface IStatusIcon {
    img: FC<React.SVGProps<SVGSVGElement>>
    tooltip?: string
}

interface IShippingStatusIcon extends IStatusIcon {
    shippingOrderType: ShippingOrderType
}

const ReservationAssetShippingStatuses: FC<IReservationAssetShippingStatusesProps> = ({
    asset,
    purpose,
    wrap,
    prependIcons,
}) => {
    const { t } = useTranslation()
    const shouldHaveReturnIcon = purpose !== Purpose.sale && purpose !== Purpose.relocation

    const formatDate = useCallback((date?: Dayjs) => {
        return date ? date.format(t('config.dateLongFormat')) : ""
    }, [t])

    const emptyDeliveryIcon = useMemo<IShippingStatusIcon>(() => {
        return {
            img: IconDeliveryNotForwardedNotReleased,
            tooltip: t('reservation.shippingStatus.delivery.notForwardedNotReleased'),
            shippingOrderType: ShippingOrderType.delivery,
        }
    }, [t])

    const emptyReturnIcon = useMemo<IShippingStatusIcon>(() => {
        return {
            img: IconReturnNotForwardedNotStored,
            tooltip: t('reservation.shippingStatus.return.notForwardedNotStored'),
            shippingOrderType: ShippingOrderType.return,
        }
    }, [t])

    const icons = useMemo<IShippingStatusIcon[]>(() => {
        const result: IShippingStatusIcon[] = []

        if (!asset.shipping) {
            result.push(emptyDeliveryIcon)
            if (shouldHaveReturnIcon) {
                result.push(emptyReturnIcon)
            }
            return result
        }

        for (let [index, assetShipping] of asset.shipping.entries()) {
            const nextAssetShipping: {
                shippingOrderType: ShippingOrderType
                shippingOrderAsset: ShippingOrderAsset
            } | undefined = asset.shipping[index + 1]
            const shippingAsset = assetShipping.shippingOrderAsset
            let img: FC | null = null
            let tooltip: string | undefined = undefined
            let shouldAppendStoredIcon = false
            if (assetShipping.shippingOrderType === ShippingOrderType.delivery) {
                if (shippingAsset.forwardToShippingDate) {
                    if (shippingAsset.isReleasedOrStored) {
                        img = shippingAsset.unplanned ? IconDeliveryNotForwardedReleased : IconDeliveryReleased
                        tooltip = t('reservation.shippingStatus.delivery.released', {
                            forwardToShippingDate: formatDate(shippingAsset.forwardToShippingDate),
                            releaseDate: formatDate(shippingAsset.releaseDate),
                        })
                    } else if (shippingAsset.forwardToShippingDate) {
                        img = IconDeliveryForwardedNotReleased
                        tooltip = t('reservation.shippingStatus.delivery.forwardedNotReleased', {
                            forwardToShippingDate: formatDate(shippingAsset.forwardToShippingDate),
                        })
                    }
                } else {
                    img = emptyDeliveryIcon.img
                    tooltip = emptyDeliveryIcon.tooltip
                }
                if (shippingAsset.isStored &&
                    (!nextAssetShipping
                        || nextAssetShipping.shippingOrderType !== ShippingOrderType.return
                        || !nextAssetShipping.shippingOrderAsset.isStored
                    )
                ) {
                    shouldAppendStoredIcon = true
                }
            } else if (assetShipping.shippingOrderType === ShippingOrderType.return) {
                if (shippingAsset.forwardToShippingDate) {
                    if (shippingAsset.isStored) {
                        img = shippingAsset.unplanned ? IconReturnNotForwardedStored : IconReturnStored
                        tooltip = t('reservation.shippingStatus.return.forwardedStored', {
                            forwardToShippingDate: formatDate(shippingAsset.forwardToShippingDate),
                            storeDate: formatDate(shippingAsset.storeDate),
                        })
                    } else if (shippingAsset.forwardToShippingDate) {
                        img = IconReturnForwardedNotStored
                        tooltip = t('reservation.shippingStatus.return.forwardedNotStored', {
                            forwardToShippingDate: formatDate(shippingAsset.forwardToShippingDate),
                        })
                    }
                } else {
                    img = emptyReturnIcon.img
                    tooltip = emptyReturnIcon.tooltip
                }
            }

            if (img && tooltip) {
                result.push({
                    img,
                    tooltip,
                    shippingOrderType: assetShipping.shippingOrderType
                })
            }

            if (shouldAppendStoredIcon) {
                result.push({
                    img: IconReturnNotForwardedStored,
                    tooltip: t('reservation.shippingStatus.return.notFowrardedStored', {
                        storeDate: formatDate(shippingAsset.storeDate),
                    }),
                    shippingOrderType: ShippingOrderType.return
                })
            }
        }

        const noShippingOrders = result.length === 0
        const oneShippingOrder = result.length === 1
        const oneShippingOrderDelivery = oneShippingOrder && result[0].shippingOrderType === ShippingOrderType.delivery
        const oneShippingOrderReturn = oneShippingOrder && result[0].shippingOrderType === ShippingOrderType.return

        const shouldPrependEmptyDeliveryIcon = oneShippingOrderReturn
        const shouldAppendEmptyDeliveryIcon = noShippingOrders
        const shouldAppendEmptyReturnIcon = shouldHaveReturnIcon && (noShippingOrders || oneShippingOrderDelivery)

        if (shouldPrependEmptyDeliveryIcon) {
            result.unshift(emptyDeliveryIcon)
        }
        if (shouldAppendEmptyDeliveryIcon) {
            result.push(emptyDeliveryIcon)
        }
        if (shouldAppendEmptyReturnIcon) {
            result.push(emptyReturnIcon)
        }

        return result
    }, [asset.shipping, emptyDeliveryIcon, emptyReturnIcon, formatDate, shouldHaveReturnIcon, t])

    function renderIcon(icon: IStatusIcon, index: number) {
        const iconEl = <icon.img className="reservation-asset-shipping-statuses__icon" />
        return (
            <FlexItem className="reservation-asset-shipping-statuses__item"
                key={index}>
                {icon.tooltip
                    ? <Tooltip
                        trigger="hover"
                        content={<div className="white-space-pre-line">{icon.tooltip}</div>}>
                        {iconEl}
                    </Tooltip>
                    : iconEl
                }
            </FlexItem>
        )
    }

    if (!purpose) {
        return null
    }

    return (
        <Flex className="reservation-asset-shipping-statuses"
            wrap={wrap ? "wrap" : undefined}>
            {!!prependIcons && prependIcons.map(renderIcon)}
            {icons.map(renderIcon)}
        </Flex>
    )
}

export default ReservationAssetShippingStatuses
